import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BookOpen, NotebookPen, DoorOpen, Check, Download, ArrowRight, Award, Users, Sparkles, Save,
} from "lucide-react";
import { Share2, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { renderAdinkraCard, shareOrDownload } from "@/lib/shareCard";
import ReferralGift from "@/components/ReferralGift";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import FilmModal from "@/components/FilmModal";
import MediaPlaceholder from "@/components/MediaPlaceholder";
import AdinkraMark from "@/components/AdinkraMark";
import AdinkraConstellation from "@/components/AdinkraConstellation";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { MODULE_FILMS, FIGURES } from "@/config/media";
import { Play } from "lucide-react";
import { Info } from "lucide-react";
import { Clapperboard } from "lucide-react";

const H = "/api/htt";

const DATA = {
  m1: { file: "modules/m1-when-someone-tells-you-something.html", hook: "The first thirty seconds — and the sentence that makes speaking worth it.", sym: "nkonsonkonson", adinkra: "Nkonsonkonson — the chain links", meaning: "\u201CWe are linked in both life and death.\u201D A symbol of unity and human bonds — how one honest response links to the next." },
  m2: { file: "modules/m2-why-nothing-changed-after-the-training.html", hook: "The four leaks — why good training changes people but not conditions.", sym: "hwehwemudua", adinkra: "Hwehwemudua — the measuring stick", meaning: "The measuring stick: a call to examine, measure and strive for the best quality — testing whether the training truly changed conditions." },
  m3: { file: "modules/m3-making-meaning-of-what-you-see.html", hook: "Interpretation as a craft — four questions that turn noticing into meaning.", sym: "nteasee", adinkra: "Nteasee — understanding", meaning: "Understanding: patient interpretation — the craft of turning what you notice into meaning before you act." },
  m4: { file: "modules/m4-the-tools-you-were-given.html", hook: "The five verbs — naming the skipped step instead of the villain.", sym: "nsaa", adinkra: "Nsaa — the hand-woven cloth", meaning: "The hand-woven cloth: excellence, authenticity and craftsmanship — using the tools you were given, and using them well." },
  m5: { file: "modules/m5-closing-the-loop-in-your-team.html", hook: "The four handovers — closing the loop, even when the answer is no.", sym: "osramnensoromma", adinkra: "Osram ne Nsoromma — the moon and star", meaning: "The moon and the star: faithfulness, love and harmony — keeping your word to the team and closing the loop." },
  m6: { file: "modules/m6-your-evidence-not-your-attendance.html", hook: "Completion counts attendance; movement measures change. Record movement.", sym: "nkyinkyim", adinkra: "Nkyinkyim — the twisting path", meaning: "The twisting path: dynamism and resourcefulness — adapting your practice, and recording movement rather than attendance." },
  m7: { file: "modules/m7-being-in-the-room.html", hook: "The four doors — a practitioner's voice in the room before the next thing lands.", sym: "mframadan", adinkra: "Mframadan — the wind-resistant house", meaning: "The wind-resistant house: resilience and readiness — a practitioner's voice built to hold steady under pressure." },
  m8: { file: "modules/m8-your-practice-picture.html", hook: "One page, honestly held — the answer you give yourself at the end.", sym: "eban", adinkra: "Eban — the fence", meaning: "The fence: love, security and safety — the boundary that protects what matters, honestly held." },
};

const MOVEMENTS = [
  { tag: "Perceive", sub: "Learning to see what is actually happening", sym: "nteasee", mods: ["m1", "m2", "m3"] },
  { tag: "Intervene", sub: "Acting where you already have permission", sym: "nsaa", mods: ["m4", "m5"] },
  { tag: "Redesign", sub: "Changing what gets recorded, bought and built", sym: "sankofa", mods: ["m6", "m7", "m8"] },
];

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const [meta, setMeta] = useState([]);
  const [pulse, setPulse] = useState(null);
  const [films, setFilms] = useState({});
  const [filmBusy, setFilmBusy] = useState({});
  const [filmModal, setFilmModal] = useState({ open: false, film: null, title: "", module: 0 });
  const [sharing, setSharing] = useState(false);
  const isAdmin = user?.role === "admin";
  const progress = user?.progress || {};
  const filmProgress = user?.film_progress || {};

  const openFilm = (num, title) => setFilmModal({ open: true, film: films[String(num)] || MODULE_FILMS[num] || null, title: `Module ${num} · ${title}`, module: num });

  const generateFilm = async (num) => {
    setFilmBusy((b) => ({ ...b, [num]: true }));
    try {
      const { data } = await api.post(`/admin/films/${num}`, {});
      toast.success(`Module ${num} film queued — it renders in the background (a few minutes).`, { duration: 6000 });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not start generation.";
      toast.error(msg, { duration: 7000 });
    } finally {
      setFilmBusy((b) => ({ ...b, [num]: false }));
    }
  };

  const saveFilmProgress = async (seconds, duration) => {
    const num = filmModal.module;
    if (!num) return;
    setUser((u) => (u ? { ...u, film_progress: { ...(u.film_progress || {}), [String(num)]: { seconds, duration } } } : u));
    try {
      await api.put("/film-progress", { module: num, seconds, duration });
    } catch (e) {}
  };

  useEffect(() => {
    api.get("/course/meta").then(({ data }) => setMeta(data.modules)).catch(() => {});
    api.get("/cohort/pulse").then(({ data }) => setPulse(data)).catch(() => {});
    api.get("/films").then(({ data }) => setFilms(data.films || {})).catch(() => {});
  }, []);

  const enterCommunity = async () => {
    let url = "https://www.skool.com/oxytocin-house-of-learning/about";
    try {
      const { data } = await api.post("/community/accept");
      if (data?.url) url = data.url;
      toast.success("Your pass to the House of Learning is open.");
    } catch (e) {}
    window.open(url, "_blank", "noopener");
  };

  const modMap = useMemo(() => {
    const m = {};
    meta.forEach((x) => (m[x.id] = x));
    return m;
  }, [meta]);

  const doneCount = user?.completed_count || 0;
  const total = user?.total_modules || 8;
  const pct = Math.round((doneCount / total) * 100);
  const completed = user?.completed;

  const firstUndone = ["m1", "m2", "m3", "m4", "m5", "m6", "m7", "m8"].find((id) => !progress[id]?.done) || "m1";

  const constellation = useMemo(
    () =>
      ["m1", "m2", "m3", "m4", "m5", "m6", "m7", "m8"].map((id) => ({
        id,
        sym: DATA[id].sym,
        n: Number(id.slice(1)),
        title: modMap[id]?.title || DATA[id].hook,
        done: !!progress[id]?.done,
      })),
    [modMap, progress]
  );

  const openModule = (id) => {
    api.put("/progress", { module: id, opened: true }).then(({ data }) => setUser(data.user)).catch(() => {});
  };

  const shareAchievement = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const blob = await renderAdinkraCard({
        symbol: "adinkrahene",
        kicker: "Course complete",
        title: "How Truth Travels",
        subtitle: "Eight modules, seven crossings — truth, well travelled.",
        awardName: user?.name || "A practitioner",
        dateStr: new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }),
      });
      await shareOrDownload(blob, "how-truth-travels-complete.png", {
        title: "How Truth Travels",
        text: "I've completed How Truth Travels — the craft of receiving difficult truth.",
      });
      toast.success("Your achievement card is ready to share.");
    } catch (e) {
      toast.error("Could not create the card. Try again.");
    } finally {
      setSharing(false);
    }
  };

  const toggleDone = async (id) => {
    const next = !progress[id]?.done;
    try {
      const { data } = await api.put("/progress", { module: id, done: next });
      setUser(data.user);
      if (next) toast.success(`Module ${modMap[id]?.n || ""} marked complete`);
      if (data.user.completed) {
        toast.success("All eight complete — well travelled.", { duration: 5000 });
      }
    } catch (e) {
      toast.error("Could not save. Try again.");
    }
  };

  const link = (rel) => `${H}/${rel}`;

  return (
    <div className="min-h-screen bg-wash">
      <SiteNav variant="dark" />

      {/* Header banner */}
      <section className="relative pt-[72px] velvet bg-purple-deep">
        <div className="absolute inset-0 opacity-40">
          <img src="/api/htt/assets/swan-platinum-photo.jpg" alt="" className="w-full h-full object-cover object-right" />
          <div className="absolute inset-0 bg-gradient-to-r from-purple-deep via-purple-deep/90 to-purple/40" />
        </div>
        <AdinkraMark
          symbol="nkyinkyim"
          className="hidden md:block absolute z-[1] text-gold pointer-events-none"
          style={{ width: "340px", height: "340px", right: "3%", top: "50%", transform: "translateY(-50%)", opacity: 0.1 }}
        />
        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-14">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-[11px] uppercase tracking-[0.24em] text-gold-pale font-bold mb-3">
            {doneCount === 0 ? "Welcome aboard" : completed ? "Your course, complete" : "Welcome back"}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-heading text-white text-[clamp(2rem,5vw,3.4rem)] leading-tight">
            {user?.name ? `${user.name},` : "Practitioner,"} how truth travels
          </motion.h1>
          <p className="mt-4 text-white/80 text-lg max-w-2xl">
            A course in receiving, interpreting and answering difficult truth — built for the moments that pass through your hands.
          </p>

          {/* progress */}
          <div className="mt-9 max-w-2xl">
            <div className="flex items-end justify-between mb-2">
              <span className="text-[12px] uppercase tracking-[0.16em] text-white/70">Your progress</span>
              <span className="font-heading text-gold-pale text-xl">{doneCount} of {total} complete</span>
            </div>
            <div className="h-2.5 rounded-full bg-white/15 overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-gold-dark to-gold"
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                data-testid="progress-bar-fill"
              />
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              {!completed && (
                <a
                  href={link(DATA[firstUndone].file)}
                  onClick={() => openModule(firstUndone)}
                  className="inline-flex items-center gap-2 bg-gold text-purple-deep font-bold rounded-full px-6 py-3 hover:bg-gold-pale transition-colors"
                  data-testid="continue-btn"
                >
                  {doneCount === 0 ? "Start Module 1" : `Continue — Module ${modMap[firstUndone]?.n || ""}`}
                  <ArrowRight className="w-4 h-4" />
                </a>
              )}
              {completed && (
                <Link to="/complete" className="inline-flex items-center gap-2 bg-gold text-purple-deep font-bold rounded-full px-6 py-3 hover:bg-gold-pale transition-colors" data-testid="view-completion-btn">
                  <Sparkles className="w-4 h-4" /> View your completion
                </Link>
              )}
              {completed && (
                <button onClick={enterCommunity} className="inline-flex items-center gap-2 border border-gold/60 text-gold-pale font-bold rounded-full px-6 py-3 hover:bg-gold hover:text-purple-deep transition-colors" data-testid="dashboard-community-btn">
                  <Users className="w-4 h-4" /> Enter the House of Learning
                </button>
              )}
            </div>
          </div>
          <p className="mt-5 inline-flex flex-wrap items-center gap-2 text-[13px] text-white/60" data-testid="reflections-note">
            <Save className="w-3.5 h-3.5 text-gold-pale" /> Your worksheet answers save to your account and travel with you across every device.
            <Link to="/practice" className="text-gold-pale font-semibold hover:text-gold underline underline-offset-4" data-testid="my-practice-link">
              View my practice →
            </Link>
            {isAdmin && (
              <Link to="/admin/leads" className="text-gold-pale font-semibold hover:text-gold underline underline-offset-4" data-testid="admin-leads-link">
                · Leads →
              </Link>
            )}
          </p>

          {/* Constellation — the eight symbols igniting gold as truth travels */}
          <div className="mt-11 border-t border-white/10 pt-9">
            <div className="flex items-end justify-between gap-4 flex-wrap mb-1">
              <p className="text-[11px] uppercase tracking-[0.2em] text-gold-pale/80 font-bold">
                {completed ? "Your constellation — complete" : "Your constellation"}
              </p>
              {completed && (
                <button
                  onClick={shareAchievement}
                  disabled={sharing}
                  className="inline-flex items-center gap-2 rounded-full border border-gold/50 text-gold-pale text-[12px] font-bold uppercase tracking-[0.12em] px-4 py-2 hover:bg-gold hover:text-purple-deep transition-colors disabled:opacity-60"
                  data-testid="share-achievement-btn"
                >
                  {sharing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
                  {sharing ? "Preparing…" : "Share your achievement"}
                </button>
              )}
            </div>
            <p className="text-white/55 text-[13px] mb-5">
              {completed
                ? "All eight lit, chained end to end — truth, well travelled. Share your journey."
                : "Each symbol lights as its module completes — a gold chain forms as truth travels the whole way."}
            </p>
            <AdinkraConstellation nodes={constellation} celebrate={completed} />
          </div>
          {completed && (
            <div className="mt-8">
              <ReferralGift variant="dark" />
            </div>
          )}
        </div>
      </section>

      {/* Modules */}
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        {MOVEMENTS.map((mv, mi) => (
          <section key={mv.tag} className="mb-16">
            <div className="flex items-center gap-4 mb-6">
              <span className="inline-flex items-center gap-2 bg-purple-mid text-white rounded-full text-[11px] uppercase tracking-[0.16em] px-4 py-1.5 font-bold">
                <AdinkraMark symbol={mv.sym} className="text-gold-pale" style={{ width: "15px", height: "15px" }} />
                {mv.tag}
              </span>
              <span className="text-lav text-[14px]">{mv.sub}</span>
              <span className="hidden sm:block flex-1 h-px bg-gradient-to-r from-line to-transparent" />
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mv.mods.map((id, idx) => {
                const m = modMap[id] || {};
                const num = Number(id.slice(1));
                const fp = filmProgress[String(num)] || {};
                const watched = fp.duration ? Math.min(100, (fp.seconds / fp.duration) * 100) : 0;
                const hasFilm = !!(films[String(num)] || MODULE_FILMS[num]);
                const filmLabel = !hasFilm ? "Film coming soon" : (watched > 2 && watched < 98 ? "Resume the film" : "Watch the film");
                const d = DATA[id];
                const isDone = !!progress[id]?.done;
                const opened = !!progress[id]?.opened;
                return (
                  <motion.div
                    key={id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.5, delay: idx * 0.05 }}
                    className={`group relative overflow-hidden rounded-2xl border p-7 flex flex-col hover:-translate-y-2 hover:shadow-[0_18px_50px_rgba(34,20,82,0.12)] transition-[transform,box-shadow,border-color,background-color] duration-300 ${
                      isDone ? "border-gold bg-cream" : "border-line bg-white"
                    }`}
                    data-testid={`module-card-${id}`}
                  >
                    <AdinkraMark
                      symbol={d.sym}
                      className="absolute text-gold pointer-events-none transition-[opacity,transform] duration-500 group-hover:scale-110"
                      style={{ width: "150px", height: "150px", right: "-26px", bottom: "-20px", opacity: isDone ? 0.12 : 0.07 }}
                    />
                    <div className="relative -mx-7 -mt-7 mb-5 h-36 overflow-hidden rounded-t-[15px] bg-purple/10">
                      <img
                        src={`${H}/rooms/assets/heroes/M${num}1.jpg`}
                        alt={m.title || `Module ${num}`}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-purple-deep/70 via-purple-deep/10 to-transparent" />
                      <button
                        onClick={() => openFilm(num, m.title || `Module ${num}`)}
                        className="group/film absolute inset-0 flex items-center justify-center"
                        data-testid={`film-btn-${id}`}
                        aria-label={`Watch the film for module ${num}`}
                      >
                        <span className="w-12 h-12 rounded-full bg-white/90 text-purple-deep flex items-center justify-center shadow-lg group-hover/film:scale-110 group-hover/film:bg-gold transition-transform">
                          <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
                        </span>
                      </button>
                      <span className="absolute bottom-2.5 left-3 text-[10px] uppercase tracking-[0.18em] text-white/90 font-bold drop-shadow" data-testid={`film-label-${id}`}>{filmLabel}</span>
                      {isAdmin && num >= 2 && !hasFilm && (
                        <button
                          onClick={(e) => { e.stopPropagation(); generateFilm(num); }}
                          disabled={!!filmBusy[num]}
                          className="absolute top-2.5 left-2.5 z-10 inline-flex items-center gap-1.5 bg-purple-deep/85 backdrop-blur text-gold-pale text-[10px] uppercase tracking-[0.14em] font-bold rounded-full px-2.5 py-1.5 border border-gold/40 hover:bg-purple-deep transition-colors disabled:opacity-60"
                          data-testid={`admin-generate-film-${id}`}
                          title="Admin — generate this module's presenter film with HeyGen"
                        >
                          <Clapperboard className="w-3 h-3" /> {filmBusy[num] ? "Queuing…" : "Generate film"}
                        </button>
                      )}
                      {watched > 1 && (
                        <div className="absolute bottom-0 inset-x-0 h-1 bg-white/20">
                          <div className="h-full bg-gold" style={{ width: `${watched}%` }} data-testid={`film-watched-${id}`} />
                        </div>
                      )}
                      {isDone && (
                        <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-gold flex items-center justify-center shadow-lg">
                          <Check className="w-4 h-4 text-purple-deep" strokeWidth={2.5} />
                        </div>
                      )}
                    </div>
                    <div className="relative z-10 flex items-center justify-between mb-3">
                      <span className="text-[11px] uppercase tracking-[0.16em] text-gold-dark font-bold">
                        Module {num}{id === "m8" ? " · Capstone" : ""}
                        {isDone ? " · ✓ done" : opened ? " · in progress" : ""}
                      </span>
                    </div>
                    <h3 className="relative z-10 font-heading text-purple text-[22px] leading-snug mb-2">{m.title}</h3>
                    <p className="relative z-10 text-[14px] text-ink/70 leading-relaxed flex-1">{d.hook}</p>
                    <TooltipProvider delayDuration={120}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="relative z-10 text-[12px] text-lav mt-3 inline-flex items-center gap-1.5 text-left hover:text-purple-mid transition-colors cursor-help"
                            data-testid={`adinkra-legend-${id}`}
                            aria-label={`Meaning of ${d.adinkra}`}
                          >
                            <AdinkraMark symbol={d.sym} className="text-gold-dark shrink-0" style={{ width: "14px", height: "14px" }} />
                            {d.adinkra}
                            <Info className="w-3 h-3 opacity-50" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" align="start" className="max-w-[260px] bg-purple-deep text-white text-[12.5px] leading-relaxed px-3.5 py-2.5 shadow-xl" data-testid={`adinkra-tip-${id}`}>
                          {d.meaning}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    <div className="relative z-10 mt-4 pt-4 border-t border-line flex flex-wrap items-center gap-4">
                      <a href={link(d.file)} onClick={() => openModule(id)} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-purple-mid hover:text-purple" data-testid={`worksheet-${id}`}>
                        <BookOpen className="w-4 h-4" /> Worksheet
                      </a>
                      <a href={link(`modules/${id}-expanded-workbook.html`)} onClick={() => openModule(id)} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-purple-mid hover:text-purple" data-testid={`workbook-${id}`}>
                        <NotebookPen className="w-4 h-4" /> Workbook
                      </a>
                      <a href={link(`rooms/${id}-practice-room.html`)} onClick={() => openModule(id)} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-gold-dark hover:text-gold" data-testid={`room-${id}`}>
                        <DoorOpen className="w-4 h-4" /> Practice room
                      </a>
                      <button
                        onClick={() => toggleDone(id)}
                        className={`ml-auto inline-flex items-center gap-1.5 text-[12px] rounded-full px-3 py-1.5 border transition-colors ${
                          isDone ? "border-gold bg-gold/15 text-gold-dark" : "border-line text-lav hover:border-purple-mid hover:text-purple"
                        }`}
                        data-testid={`done-toggle-${id}`}
                      >
                        <Check className="w-3.5 h-3.5" /> {isDone ? "Done" : "Mark done"}
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </section>
        ))}

        {/* Figures & evidence (placeholders to be supplied) */}
        <section className="mb-10" data-testid="figures-strip">
          <div className="flex items-center gap-4 mb-6">
            <span className="inline-flex items-center gap-2 bg-purple-mid text-white rounded-full text-[11px] uppercase tracking-[0.16em] px-4 py-1.5 font-bold">
              <AdinkraMark symbol="adinkrahene" className="text-gold-pale" style={{ width: "15px", height: "15px" }} />
              Figures &amp; evidence
            </span>
            <span className="text-lav text-[14px]">The diagrams that make the ideas concrete</span>
            <span className="hidden sm:block flex-1 h-px bg-gradient-to-r from-line to-transparent" />
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {FIGURES.map((f) => (
              <MediaPlaceholder
                key={f.id}
                src={f.src}
                label={f.label}
                caption={f.caption}
                kind={f.id === "practice-picture" ? "graph" : "figure"}
                testid={`figure-${f.id}`}
              />
            ))}
          </div>
        </section>

        {/* Cohort Wall */}
        {pulse && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-10 rounded-2xl border border-line bg-white p-7 flex flex-col sm:flex-row sm:items-center gap-6"
            data-testid="cohort-wall"
          >
            <div className="flex-1">
              <p className="text-[11px] uppercase tracking-[0.2em] text-gold-dark font-bold mb-1">The founding cohort</p>
              <h3 className="font-heading text-2xl text-purple">
                {pulse.completed_this_week > 0
                  ? `${pulse.completed_this_week} ${pulse.completed_this_week === 1 ? "practitioner" : "practitioners"} completed the course this week`
                  : "You're among the founding practitioners walking this path"}
              </h3>
              <p className="text-[14px] text-lav mt-1">
                {pulse.enrolled} {pulse.enrolled === 1 ? "practitioner has" : "practitioners have"} joined the cohort. You don't walk it alone.
              </p>
            </div>
            <div className="flex items-center gap-3">
              {pulse.recent.length > 0 ? (
                <div className="flex -space-x-3">
                  {pulse.recent.map((r, i) => (
                    <div
                      key={i}
                      title={`A practitioner · ${r.ago}`}
                      className="w-11 h-11 rounded-full bg-purple-mid text-white font-heading text-lg flex items-center justify-center ring-2 ring-white"
                    >
                      {r.initial}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex -space-x-3 opacity-60">
                  {["·", "·", "·"].map((c, i) => (
                    <div key={i} className="w-11 h-11 rounded-full bg-wash border border-line text-lav flex items-center justify-center ring-2 ring-white">{c}</div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Take it with you + completion */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-line bg-white p-8">
            <h2 className="font-heading text-2xl text-purple mb-1">Take it with you</h2>
            <div className="gold-rule w-14 mb-5" />
            <ul className="space-y-4">
              <li>
                <a href={`${H}/downloads/the-first-thirty-seconds-pocket-guide.pdf`} className="inline-flex items-center gap-2 text-purple-mid font-medium hover:text-purple" data-testid="download-pocket-guide">
                  <Download className="w-4 h-4" /> The First Thirty Seconds — Pocket Guide
                </a>
                <p className="text-[12px] text-lav ml-6">PDF · print it, fold it, keep it in a pocket on shift</p>
              </li>
              <li>
                <a href={`${H}/downloads/when-someone-tells-you-something-worksheet.pdf`} className="inline-flex items-center gap-2 text-purple-mid font-medium hover:text-purple" data-testid="download-worksheet">
                  <Download className="w-4 h-4" /> When Someone Tells You Something — Worksheet
                </a>
                <p className="text-[12px] text-lav ml-6">PDF · the Module 1 worksheet for working on paper</p>
              </li>
              <li>
                <a href={`${H}/rooms/complete-course.html`} className="inline-flex items-center gap-2 text-purple-mid font-medium hover:text-purple" data-testid="complete-course-link">
                  <DoorOpen className="w-4 h-4" /> The Complete Course — all eight practice rooms
                </a>
                <p className="text-[12px] text-lav ml-6">Every practice room end to end, with the Read-aloud voice throughout</p>
              </li>
            </ul>
          </div>

          <div className="relative rounded-2xl border border-gold bg-purple-deep velvet p-8 text-white overflow-hidden">
            <AdinkraMark
              symbol="gyenyame"
              className="absolute text-gold pointer-events-none"
              style={{ width: "220px", height: "220px", right: "-40px", bottom: "-40px", opacity: 0.14 }}
            />
            <div className="relative z-10">
              <h2 className="font-heading text-2xl text-white mb-1">On completion</h2>
              <div className="gold-rule w-14 mb-5" />
              <p className="text-white/80 text-[15px] leading-relaxed mb-5">
                Finish all eight and your CPD certificate is issued in your name, the doors to the Oxytocin House of Learning open, and your £39 bridges you into Phoenix Rising.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to={completed ? "/certificate" : "#"}
                  onClick={(e) => { if (!completed) { e.preventDefault(); toast("Complete all eight modules to unlock your certificate."); } }}
                  className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-semibold text-sm transition-colors ${completed ? "bg-gold text-purple-deep hover:bg-gold-pale" : "bg-white/10 text-white/60 cursor-not-allowed"}`}
                  data-testid="certificate-cta"
                >
                  <Award className="w-4 h-4" /> {completed ? "View certificate" : "Certificate locked"}
                </Link>
                <button onClick={enterCommunity} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-semibold text-sm border border-white/25 text-white hover:bg-white/10 transition-colors" data-testid="community-link">
                  <Users className="w-4 h-4" /> The community
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
      <FilmModal
        open={filmModal.open}
        film={filmModal.film}
        title={filmModal.title}
        resumeAt={(filmProgress[String(filmModal.module)] || {}).seconds || 0}
        onProgress={saveFilmProgress}
        onClose={() => setFilmModal({ open: false, film: null, title: "", module: 0 })}
      />
    </div>
  );
}
